import { useRoute, useNavigation } from "@react-navigation/native";
import {
  StyleSheet,
  Text,
  View,
  FlatList,
  TextInput,
  TouchableOpacity,
  Image,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  Dimensions,
  ActivityIndicator,
} from "react-native";
import React, { useState, useRef, useEffect } from "react";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { COLORS, FONTS, SIZES } from "../../../utils";
import BasicHeader from "../../components/BasicHeader";
import useData from "../../hooks/useData";
import * as Font from "expo-font";
import {
  createMessage,
  getProductMessages,
} from "../../apiCall/ProductRequestApiCall";
import { useSafeAreaInsets } from "react-native-safe-area-context";

// Message Bubble Component
const MessageBubble = ({ message }) => {
  const isBuyer = message.sender_type === "user";

  return (
    <View
      style={[
        styles.messageContainer,
        isBuyer ? styles.buyerMessage : styles.sellerMessage,
      ]}
    >
      <View
        style={[
          styles.messageBubble,
          isBuyer ? styles.buyerBubble : styles.sellerBubble,
        ]}
      >
        <Text
          style={[
            styles.messageText,
            isBuyer ? styles.buyerText : styles.sellerText,
          ]}
        >
          {message.message}
        </Text>
      </View>
      <Text style={styles.messageTime}>
        {formatMessageTime(message.created_at)}
      </Text>
    </View>
  );
};

// Format timestamp to readable time
const formatMessageTime = (timestamp) => {
  const date = new Date(timestamp);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
};

export default function ProductChat() {
  const route = useRoute();
  const { selectedProduct } = useData();
  const navigation = useNavigation();
  const { productId } = route.params;
  const insets = useSafeAreaInsets(); // Safe area insets for notch devices

  const [chatData, setChatData] = useState({
    messages: [],
    product: null,
    loading: true,
    error: null,
  });
  const [newMessage, setNewMessage] = useState("");
  const flatListRef = useRef(null);

  // Fetch product messages
  useEffect(() => {
    fetchProductMessages();
  }, [productId]);

  const fetchProductMessages = async () => {
    try {
      setChatData((prev) => ({ ...prev, loading: true, error: null }));
      const response = await getProductMessages(productId);

      if (response.status) {
        console.log(response);
        // Transform API response to match our component structure
        const transformedMessages = response.messages.map((msg) => ({
          id: msg.id,
          message: msg.message,
          sender_type: msg.user_id ? "user" : "admin", // user is buyer, admin is seller
          created_at: msg.created_at,
          // Additional fields for display
          user_name: msg.user_name,
          admin_name: msg.admin_name,
        }));

        setChatData({
          messages: transformedMessages,
          product: response.product,
          loading: false,
          error: null,
        });
      } else {
        setChatData((prev) => ({
          ...prev,
          loading: false,
          error: "Failed to load messages",
        }));
      }
    } catch (error) {
      console.error("Error fetching messages:", error);
      setChatData((prev) => ({
        ...prev,
        loading: false,
        error: "Error loading messages",
      }));
    }
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (flatListRef.current && chatData.messages.length > 0) {
      setTimeout(() => {
        flatListRef.current?.scrollToEnd({ animated: true });
      }, 100);
    }
  }, [chatData.messages]);

  useEffect(() => {
    (async () => {
      await Font.loadAsync(MaterialIcons.font);
    })();
  }, []);

  const sendMessage = () => {
    if (newMessage.trim() === "") return;

    // Create temporary message
    const newMsg = {
      id: Date.now().toString(), // temporary ID
      message: newMessage.trim(),
      sender_type: "user", // assuming current user is sending
      created_at: new Date().toISOString(),
      user_name: "You",
      product_id: productId,
      admin_id: null,
    };

    createMessage(newMsg)
      .then((res) => {
        console.log({ res });
        if (res.status) {
          setChatData((prev) => ({
            ...prev,
            messages: [...prev.messages, newMsg],
          }));
          setNewMessage("");
        }
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const renderMessage = ({ item }) => <MessageBubble message={item} />;

  // Web optimized layout
  const isWeb = Platform.OS === "web";
  const { width } = Dimensions.get("window");
  const isLargeScreen = width > 768;

  // Use product data from API or fallback
  const product = chatData.product ||
    selectedProduct || {
      id: productId,
      productName: "Product",
      manufacturer: "Manufacturer",
      description: "Product description",
      url: "https://example.com",
    };

  if (chatData.loading) {
    return (
      <View style={[styles.container, { paddingTop: insets.top }]}>
        <BasicHeader title="Loading..." />
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={COLORS.primary} />
          <Text style={styles.loadingText}>Loading messages...</Text>
        </View>
      </View>
    );
  }

  if (chatData.error) {
    return (
      <View style={[styles.container, { paddingTop: insets.top }]}>
        <BasicHeader title="Error" />
        <View style={styles.errorContainer}>
          <MaterialIcons name="error-outline" size={48} color={COLORS.error} />
          <Text style={styles.errorText}>{chatData.error}</Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={fetchProductMessages}
          >
            <Text style={styles.retryButtonText}>Retry</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      style={{ flex: 1 }}
      behavior={Platform.OS === "ios" ? "padding" : "height"}
      keyboardVerticalOffset={Platform.OS === "ios" ? 50 : 0} // adjust for your header height
    >
      <View style={styles.container}>
        <BasicHeader
          title={product.productName}
          onRefresh={fetchProductMessages}
        />

        <View
          style={[
            styles.mainContent,
            isWeb && styles.mainContentWeb,
            isLargeScreen && styles.mainContentLarge,
          ]}
        >
          {/* Product Info */}
          <View style={styles.productSection}>
            <View style={[styles.productCard, isWeb && styles.productCardWeb]}>
              <Image
                source={{
                  uri: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSNAkQa9nHgRUWiCDunA1ARJaPBogJeI0GgLw&s",
                }}
                style={styles.productImage}
              />
              <View style={styles.productInfo}>
                <View
                  style={{
                    flexDirection: "row",
                    justifyContent: "space-between",
                  }}
                >
                  <Text style={styles.manufacturer}>
                    {product.manufacturer}
                  </Text>
                  {product.created_at && (
                    <View style={styles.metaItem}>
                      <MaterialIcons
                        name="calendar-today"
                        size={14}
                        color={COLORS.gray}
                      />
                      <Text style={styles.metaText}>
                        {new Date(product.created_at).toLocaleDateString()}
                      </Text>
                    </View>
                  )}
                </View>
                <Text style={styles.productName}>{product.productName}</Text>
                <Text style={styles.description} numberOfLines={2}>
                  {product.description}
                </Text>
              </View>
            </View>
          </View>

         
            <View
              style={[styles.chatContainer, isWeb && styles.chatContainerWeb]}
            >
              {chatData.messages.length === 0 ? (
                <View style={styles.emptyContainer}>
                  <MaterialIcons name="chat" size={48} color={COLORS.gray} />
                  <Text style={styles.emptyText}>No messages yet</Text>
                  <Text style={styles.emptySubText}>
                    Start a conversation about this product
                  </Text>
                </View>
              ) : (
                <FlatList
                  ref={flatListRef}
                  data={chatData.messages}
                  renderItem={renderMessage}
                  keyExtractor={(item) => item.id.toString()}
                  style={styles.messagesList}
                  showsVerticalScrollIndicator={isWeb}
                  contentContainerStyle={styles.messagesContainer}
                  onContentSizeChange={() =>
                    flatListRef.current?.scrollToEnd({ animated: false })
                  }
                />
              )}
            </View>
        

          {/* Input Section */}
          <View
            style={[
              styles.inputContainer,
              isWeb && styles.inputContainerWeb,
              { marginBottom: insets.bottom },
            ]}
          >
            <TextInput
              style={styles.textInput}
              value={newMessage}
              onChangeText={setNewMessage}
              placeholder="Type your message..."
              placeholderTextColor={COLORS.black}
              multiline
              returnKeyType="send"
              blurOnSubmit={false}
            />
            <TouchableOpacity
              style={[
                styles.sendButton,
                newMessage.trim() === "" && styles.sendButtonDisabled,
              ]}
              onPress={sendMessage}
              disabled={newMessage.trim() === ""}
            >
              <Image
                source={require("../../assets/Icons/send.png")}
                style={{ width: 30, height: 30 }}
              />
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: COLORS.background,
  },
  // Main content wrapper for web optimization
  mainContent: {
    flex: 1,
  },
  mainContentWeb: {
    maxWidth: 800,
    alignSelf: "center",
    width: "100%",
  },
  mainContentLarge: {
    maxWidth: 1000,
  },
  productSection: {
    flex: 0,
    padding: 16,
    paddingBottom: 8,
  },
  productCard: {
    backgroundColor: COLORS.white,
    borderRadius: 12,
    padding: 12,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
    flexDirection: "row",
    alignItems: "center",
  },
  productCardWeb: {
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 6,
  },
  productImage: {
    width: 60,
    height: 60,
    borderRadius: 8,
    marginRight: 12,
  },
  productInfo: {
    flex: 1,
  },
  manufacturer: {
    fontSize: 12,
    fontFamily: FONTS.PoppinsMedium,
    color: COLORS.gray,
    textTransform: "uppercase",
  },
  productName: {
    fontSize: 16,
    fontFamily: FONTS.PoppinsBold,
    color: COLORS.dark,
    marginTop: 2,
  },
  description: {
    fontSize: 12,
    fontFamily: FONTS.PoppinsRegular,
    color: COLORS.dark,
    lineHeight: 16,
  },
  metaItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    alignSelf: "flex-end",
  },
  metaText: {
    fontSize: 11,
    fontFamily: FONTS.PoppinsRegular,
    color: COLORS.gray,
  },
  chatContainer: {
    flex: 1,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    backgroundColor: COLORS.white,
    padding: 16,
    paddingBottom: 0, // We'll handle bottom padding with safe area
  },
  chatContainerWeb: {
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    marginTop: 100,
    marginHorizontal: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: -2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 8,
  },
  messagesList: {
    flex: 1,
  },
  messagesContainer: {
    paddingBottom: 10,
  },
  messageContainer: {
    marginVertical: 6,
    maxWidth: "80%",
  },
  buyerMessage: {
    alignSelf: "flex-end",
    alignItems: "flex-end",
  },
  sellerMessage: {
    alignSelf: "flex-start",
    alignItems: "flex-start",
  },
  messageBubble: {
    padding: 12,
    borderRadius: 16,
    marginBottom: 4,
  },
  buyerBubble: {
    backgroundColor: COLORS.primary,
    borderBottomRightRadius: 4,
  },
  sellerBubble: {
    backgroundColor: "#f1f1f1",
    borderBottomLeftRadius: 4,
  },
  messageText: {
    fontSize: 14,
    fontFamily: FONTS.PoppinsRegular,
    lineHeight: 18,
  },
  buyerText: {
    color: COLORS.white,
  },
  sellerText: {
    color: COLORS.dark,
  },
  messageTime: {
    fontSize: 10,
    fontFamily: FONTS.PoppinsRegular,
    color: COLORS.gray,
    marginTop: 2,
  },
  inputContainer: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#f8f8f8",
    borderRadius: 25,
    paddingHorizontal: 16,
    paddingVertical: 8,
    marginTop: 10,
    marginBottom: 10, // Base margin
  },
  inputContainerWeb: {
    backgroundColor: "#f5f5f5",
    borderWidth: 1,
    borderColor: "#e0e0e0",
    borderRadius: 30,
    paddingHorizontal: 20,
    paddingVertical: 12,
  },
  textInput: {
    flex: 1,
    fontSize: 14,
    fontFamily: FONTS.PoppinsRegular,
    color: COLORS.dark,
    maxHeight: 80,
    paddingVertical: 8,
  },
  sendButton: {
    padding: 8,
    marginLeft: 8,
    backgroundColor: "rgba(0,0,0,0.1)",
    borderRadius: 50,
  },
  sendButtonDisabled: {
    opacity: 0.5,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingText: {
    marginTop: 12,
    fontFamily: FONTS.PoppinsRegular,
    color: COLORS.gray,
  },
  errorContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  errorText: {
    marginTop: 12,
    fontFamily: FONTS.PoppinsRegular,
    color: COLORS.error,
    textAlign: "center",
  },
  retryButton: {
    marginTop: 16,
    backgroundColor: COLORS.primary,
    paddingHorizontal: 20,
    paddingVertical: 10,
    borderRadius: 8,
  },
  retryButtonText: {
    color: COLORS.white,
    fontFamily: FONTS.PoppinsSemiBold,
  },
  emptyContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  emptyText: {
    marginTop: 12,
    fontFamily: FONTS.PoppinsSemiBold,
    color: COLORS.dark,
  },
  emptySubText: {
    marginTop: 4,
    fontFamily: FONTS.PoppinsRegular,
    color: COLORS.gray,
    textAlign: "center",
  },
});
